/* eslint-disable max-lines */
// Modules
import { io, Socket } from "socket.io-client";

// Types
import { AvailableType, CallType, DishLogType, MindrayCredentialsAvailableEvent, MindrayCredentialsListEvent, } from './../dish.types';
import { UserType } from '../../../types/user.type';
import { ChatMessageType } from "../components/room/chat/chat.types";

// Environment variables
import { environment } from '../../../environment';

// Components
import { PtzCapabilities, PtzDeviceInfo, SocketAvailable } from './socket.available';

// Audio Assets
const messageAudio = require('./message.mp3');

// Socket Status
enum SocketStatus {
    CONNECTED = 'connected',
    DISCONNECTED = 'disconnected',
    ERROR_CONNECTING = 'error connecting',
}

// Socket Instance Interface
interface SocketInstanceInterface {
    debug?: boolean;
    roomId: string | undefined;
    userId: string;
    user: UserType;
    userName: string;
    localCall: boolean;
    onLog?: (log: DishLogType) => void;
    reconnectToRoom: (roomId: string) => void;
    onConnect?: () => void;
    onSocketId?: (socketId: string) => void;
    onDisconnect?: () => void;
    onErrorConnecting?: () => void;
    askCloseSession: (ask: boolean) => void;
    onNewLocalStream?: (stream: MediaStream, type?: {type: string}) => void;
    onRemoveLocalStream?: (streamIndex: number) => void;
    onUpdateParticipants?: (participants: SocketParticipants) => void;
    onMessage?: (message: ChatMessageType) => void;
    onRemoveStreamParticipant?: (streamId: string) => void;
    onAvailables: (availables: AvailableType[]) => void
    onRoom: (room: string) => void
    onCall: (call: CallType) => void
    onAccepted: (call: CallType) => void
    onRejected: (call: CallType) => void
    onCanceled: (call: CallType) => void
    closeSession: () => void
    onChangeLayout: (layout: string) => void;
    onRemoveStream: (streamId: string) => void;
    onChangeStreamStatus: (streamId: string, status: boolean, type: string) => void;
    onFileShare: (username: string, file: string, filename: string, fileUUID: string) => void;
    onChangeStreamTrackStatus: (streamId: string, trackId: string, status: boolean, type: 'audio' | 'video', roomId?: string, streamType?: string) => void;
    onChangeVolume: (volume: number) => void;
    onUpdateLocalStream: (newStream: MediaStreamTrack) => void;
    onNoLongerActive: (socketId: string, username: string) => void;
    onPtzDevicesListReceived: (devices: PtzDeviceInfo[]) => void;
    onPtzCommandMove: (deviceId: string, action: 'pan' | 'tilt' | 'zoom', value: number) => void;
    onPtzCapabilitiesReceived: (deviceId: string, capabilities: PtzCapabilities) => void;
    onMindrayCredentialsAvailable?: (data: MindrayCredentialsAvailableEvent) => void;
    onMindrayCredentialsList?: (data: MindrayCredentialsListEvent) => void;
}

// Socket Participants Type
export type SocketParticipants = {
    [userId: string]: {
        peer: RTCPeerConnection[],
        user: UserType,
        streams: MediaStream[]
    }
}

/**
 * Socket Instance
 * @description Socket to video conference
 * @param {SocketInstanceInterface} properties
 * @returns {SocketInstance}
 */
export class SocketInstance {
    private localMediaStreams: MediaStream[] = [];
    private streamTypes: { [streamId: string]: string } = {};
    private streamManual: { [streamId: string]: string } = {};
    private socket?: Socket;
    private debug: boolean;
    private roomId: string | undefined;
    private userId: string;
    private senders: { [streamId: string]: RTCRtpSender } = {};
    private userName: string;
    private user: UserType;
    private getCandidatesHistory: { [key: string]: boolean } = {}
    private participants: SocketParticipants = {}
    public status: SocketStatus = SocketStatus.DISCONNECTED;
    private localCall: boolean;
    private events: {
        onLog?: (log: DishLogType) => void;
        onConnect?: () => void;
        onSocketId?: (socketId: string) => void;
        onDisconnect?: () => void;
        onErrorConnecting?: () => void;
        onNewLocalStream?: (stream: MediaStream, type?: {type: string}, manual?: {manual_id: string}) => void;
        onUpdateParticipants?: (data: SocketParticipants) => void;
        onMessage?: (message: ChatMessageType) => void;
        onRemoveLocalStream?: (streamIndex: number) => void;
        onRemoveStreamParticipant?: (streamId: string) => void;
        onAvailables: (availables: AvailableType[]) => void
        onRoom: (room: string) => void
        onCall: (call: CallType) => void
        onAccepted: (call: CallType) => void
        onRejected: (call: CallType) => void
        onCanceled: (call: CallType) => void
        closeSession: () => void
        askCloseSession: (ask: boolean) => void;
        reconnectToRoom: (roomId: string) => void;
        onChangeLayout: (layout: string) => void;
        onRemoveStream: (streamId: string) => void;
        onChangeStreamStatus: (streamId: string, status: boolean, type: string) => void;
        onFileShare: (username: string, file: string, filename: string, fileUUID: string) => void;
        onChangeStreamTrackStatus: (streamId: string, trackId: string, status: boolean, type: 'audio' | 'video', roomId?: string, streamType?: string) => void;
        onChangeVolume: (volume: number) => void;
        onUpdateLocalStream: (stream: MediaStreamTrack) => void;
        onNoLongerActive: (socketId: string, username: string) => void;
        onPtzDevicesListReceived: (devices: PtzDeviceInfo[]) => void;
        onPtzCommandMove: (deviceId: string, action: 'pan' | 'tilt' | 'zoom', value: number) => void;
        onPtzCapabilitiesReceived: (deviceId: string, capabilities: PtzCapabilities) => void;
        onMindrayCredentialsAvailable?: (data: MindrayCredentialsAvailableEvent) => void;
        onMindrayCredentialsList?: (data: MindrayCredentialsListEvent) => void;
    }
    private _latency: number = 0;
    private _latencySamples: number[] = [];
    private _maxSamples: number = 10;
    private _jitter: number = 0;
    private _prevLatency: number | null = null;
  // packet loss and speed measurements removed
  private pingInterval: NodeJS.Timeout | null = null;

    constructor({
        onAvailables,
        onRoom,
        onCall,
        onAccepted,
        onRejected,
        onCanceled,
        closeSession,
        askCloseSession,
        reconnectToRoom,
        onChangeLayout,
        onRemoveStream,
        onChangeStreamStatus,
        onChangeStreamTrackStatus,
        onChangeVolume,
        onFileShare,
        onUpdateLocalStream,
        onNoLongerActive,
        onPtzDevicesListReceived,
        onPtzCommandMove,
        onPtzCapabilitiesReceived,
        onMindrayCredentialsAvailable,
        onMindrayCredentialsList,
        user, debug, roomId, userId, userName, onLog, onRemoveStreamParticipant, onRemoveLocalStream, onConnect, onSocketId, onDisconnect, onErrorConnecting, onNewLocalStream, onUpdateParticipants, onMessage }: SocketInstanceInterface) {

        this.debug = debug || false;
        this.roomId = roomId;
        this.userId = userId;
        this.userName = userName;

        this.user = user;

        this.localCall = localStorage.getItem('localCall') === 'true' ? true : false;

        this.events = {
            onLog,
            reconnectToRoom,
            onConnect,
            onSocketId,
            onDisconnect,
            onErrorConnecting,
            onNewLocalStream,
            onUpdateParticipants,
            closeSession,
            onMessage,
            onRemoveLocalStream,
            onRemoveStreamParticipant,
            onAvailables,
            onRoom,
            onCall,
            onAccepted,
            onRejected,
            onCanceled,
            askCloseSession,
            onChangeLayout,
            onRemoveStream,
            onChangeStreamStatus,
            onFileShare,
            onChangeStreamTrackStatus,
            onChangeVolume,
            onUpdateLocalStream,
            onNoLongerActive,
            onPtzDevicesListReceived,
            onPtzCommandMove,
            onPtzCapabilitiesReceived,
            onMindrayCredentialsAvailable,
            onMindrayCredentialsList,
        }
        this.socket = this.connect();

        // events of socket
        this.onConnectFailed()
        this.onConnect()
        this.onDisconnect()
        this.onAllUsers()
        this.getOffer()
        this.onMessage()
        this.onError()
        this.onConnectError()

    }

    get latency(): number {
      return this._latency;
    }

    get jitter(): number {
      return this._jitter;
    }

    // download/upload/packetLoss getters removed

    startPingInterval() {
      if (this.pingInterval) {
        clearInterval(this.pingInterval);
      }

      // Adjust ping interval based on connection quality to reduce overhead
      // Bad connections: ping less frequently to save bandwidth
      const getInterval = () => {
        const quality = this.getConnectionQuality();
        switch(quality) {
          case 'excellent': return 3000;  // 3s - fast adaptation
          case 'good': return 5000;       // 5s - normal
          case 'moderate': return 8000;   // 8s - reduce overhead
          case 'bad': return 15000;       // 15s - minimize bandwidth usage
          default: return 5000;
        }
      };

      // Run latency/jitter tests immediately
      this.measureLatency();

      // Schedule periodic latency/jitter measurements with dynamic interval
      this.pingInterval = setInterval(() => {
        this.measureLatency();

        // Dynamically adjust interval if quality changed significantly
        const currentInterval = getInterval();
        if (this.pingInterval && currentInterval !== 5000) {
          clearInterval(this.pingInterval);
          this.pingInterval = setInterval(() => this.measureLatency(), currentInterval);
        }
      }, getInterval());
    }

    stopPingInterval() {
      if (this.pingInterval) {
        clearInterval(this.pingInterval);
        this.pingInterval = null;
      }
    }

    measureLatency() {
      const start = performance.now();

      this.socket?.emit('ping:basic', () => {
        const currentLatency = performance.now() - start;
        this._latencySamples.push(currentLatency);

        // Calculate jitter
        if (this._prevLatency !== null) {
          const instantJitter = Math.abs(currentLatency - this._prevLatency);
          // Smooth jitter with 1/16 coefficient (RFC 3550 recommendation)
          this._jitter = this._jitter + (instantJitter - this._jitter) / 16;
        }
        this._prevLatency = currentLatency;

        // Keep only the last N samples
        if (this._latencySamples.length > this._maxSamples) {
          this._latencySamples.shift();
        }

        // Calculate median latency
        const sortedSamples = [...this._latencySamples].sort((a, b) => a - b);
        const mid = Math.floor(sortedSamples.length / 2);

        if (sortedSamples.length % 2 === 0) {
          this._latency = (sortedSamples[mid - 1] + sortedSamples[mid]) / 2;
        } else {
          this._latency = sortedSamples[mid];
        }

        // Adjust bitrate dynamically based on connection quality changes
        this.adjustBitrateForAllSenders();
      });
    }

    /**
     * Adjust bitrate for all active senders based on current connection quality
     */
    private adjustBitrateForAllSenders() {
      const optimalBitrate = this.getOptimalBitrate();

      Object.keys(this.senders).forEach(async (trackId) => {
        const sender = this.senders[trackId];
        if (!sender || !sender.track) return;

        try {
          const parameters = sender.getParameters();
          if (!parameters.encodings || parameters.encodings.length === 0) return;

          let hasChanges = false;

          if (sender.track.kind === 'video') {
            const currentMaxBitrate = parameters.encodings[0].maxBitrate;

            // Only update if there's a significant change (>20% difference)
            if (!currentMaxBitrate || Math.abs(currentMaxBitrate - optimalBitrate.video) / optimalBitrate.video > 0.2) {
              parameters.encodings[0].maxBitrate = optimalBitrate.video;
              parameters.encodings[0].maxFramerate = optimalBitrate.maxFramerate;
              parameters.encodings[0].scaleResolutionDownBy =
                optimalBitrate.video < 500000 ? 2 : 1;
              hasChanges = true;
            }
          } else if (sender.track.kind === 'audio') {
            const currentMaxBitrate = parameters.encodings[0].maxBitrate;

            if (!currentMaxBitrate || Math.abs(currentMaxBitrate - optimalBitrate.audio) / optimalBitrate.audio > 0.2) {
              parameters.encodings[0].maxBitrate = optimalBitrate.audio;
              hasChanges = true;
            }
          }

          if (hasChanges) {
            await sender.setParameters(parameters);
            const bitrate = parameters.encodings[0]?.maxBitrate || 0;
            const fps = parameters.encodings[0]?.maxFramerate || 0;
            const scale = parameters.encodings[0]?.scaleResolutionDownBy || 1;

            console.log(`[Bitrate Adjustment] ${sender.track.kind.toUpperCase()}: ${(bitrate / 1000).toFixed(0)}kbps${sender.track.kind === 'video' ? ` @ ${fps}fps (scale: ${scale}x)` : ''} | Quality: ${this.getConnectionQuality().toUpperCase()}`);
            this.log(`Bitrate adjusted: ${sender.track.kind} to ${bitrate / 1000}kbps (Quality: ${this.getConnectionQuality()})`);
          }
        } catch (error) {
          // Silently fail - some senders might be in invalid state
        }
      });
    }

    // download/upload/packet loss measurement functions removed

    getConnectionQuality(): string {
      // Evaluate quality only based on latency and jitter now
      if (this._latency < 50 && this._jitter < 10) return 'excellent';
      if (this._latency < 100 && this._jitter < 20) return 'good';
      if (this._latency < 300 && this._jitter < 50) return 'moderate';
      return 'bad';
    }

    /**
     * Modify SDP to request lower bitrate from remote peers
     * This helps reduce incoming bandwidth consumption
     */
    private modifySdpBitrate(sdp: RTCSessionDescriptionInit): RTCSessionDescriptionInit {
      if (!sdp.sdp) return sdp;

      const quality = this.getConnectionQuality();
      const optimalBitrate = this.getOptimalBitrate();

      // Set b=AS (Application Specific) bandwidth limit in SDP
      // This tells the remote peer to not exceed this bitrate
      const maxBandwidthKbps = Math.floor((optimalBitrate.video + optimalBitrate.audio) / 1000);

      let modifiedSdp = sdp.sdp;

      // Add bandwidth limitation to video sections
      modifiedSdp = modifiedSdp.replace(
        /(m=video.*\r\n)/g,
        `$1b=AS:${maxBandwidthKbps}\r\n`
      );

      // Add TIAS (Transport Independent Application Specific) for better compatibility
      modifiedSdp = modifiedSdp.replace(
        /(m=video.*\r\n)/g,
        `$1b=TIAS:${optimalBitrate.video}\r\n`
      );

      console.log(`[SDP Modification] Requesting max ${maxBandwidthKbps}kbps from remote peer | Quality: ${quality.toUpperCase()}`);

      return { ...sdp, sdp: modifiedSdp };
    }

    /**
     * Calculate optimal bitrate based on connection quality
     * Returns bitrate in bps (bits per second)
     */
    getOptimalBitrate(): { video: number; audio: number; maxFramerate: number } {
      const quality = this.getConnectionQuality();

      // Bitrate configurations for different quality levels
      const bitrateConfig: { [key: string]: { video: number; audio: number; maxFramerate: number } } = {
        excellent: {
          video: 2500000,  // 2.5 Mbps - Alta calidad
          audio: 320000,   // 320 kbps
          maxFramerate: 30
        },
        good: {
          video: 800000,   // 800 kbps - Buena calidad
          audio: 96000,    // 96 kbps
          maxFramerate: 25
        },
        moderate: {
          video: 400000,   // 400 kbps - Calidad media (ideal para Starlink)
          audio: 64000,    // 64 kbps
          maxFramerate: 15
        },
        bad: {
          video: 250000,   // 250 kbps - Calidad baja pero estable
          audio: 32000,    // 32 kbps
          maxFramerate: 10
        }
      };

      return bitrateConfig[quality];
    }

    async shareLocalStreamsToRemoteUser(peer: RTCPeerConnection, stream: MediaStream, type?: {type: string}, manual?: {manual_id: string}) {
      let typeToCheck = type?.type;

      if (!typeToCheck) {
        typeToCheck = this.streamTypes[stream.id];
      }

      // Get optimal bitrate based on current connection quality
      const optimalBitrate = this.getOptimalBitrate();

      stream.getTracks().forEach(async (track) => {
        if (track.kind === 'audio' && localStorage.getItem('audio_status') === 'false') {
          track.enabled = false;
        }

        if (track.kind === 'video' && localStorage.getItem('video_status') === 'false') {
          track.enabled = false;
        }

        if (
          typeToCheck === 'share.screen' ||
          typeToCheck === 'media' ||
          typeToCheck === 'med.device'
        ) {
          track.enabled = true;
        }

        const sender = peer.addTrack(track, stream);
        this.senders[track.id] = sender;

        // Apply bitrate restrictions based on connection quality
        try {
          const parameters = sender.getParameters();
          if (!parameters.encodings || parameters.encodings.length === 0) {
            parameters.encodings = [{}];
          }

          if (track.kind === 'video') {
            parameters.encodings[0].maxBitrate = optimalBitrate.video;
            parameters.encodings[0].maxFramerate = optimalBitrate.maxFramerate;

            // Configuración adicional para mejorar estabilidad
            parameters.encodings[0].scaleResolutionDownBy =
              optimalBitrate.video < 500000 ? 2 : 1; // Reducir resolución si bitrate < 500kbps

            await sender.setParameters(parameters);

            console.log(`[Initial Bitrate] VIDEO: ${(optimalBitrate.video / 1000).toFixed(0)}kbps @ ${optimalBitrate.maxFramerate}fps (scale: ${parameters.encodings[0].scaleResolutionDownBy || 1}x) | Quality: ${this.getConnectionQuality().toUpperCase()}`);
            this.log(`Video bitrate set to ${optimalBitrate.video / 1000}kbps @ ${optimalBitrate.maxFramerate}fps (Quality: ${this.getConnectionQuality()})`);
          } else if (track.kind === 'audio') {
            parameters.encodings[0].maxBitrate = optimalBitrate.audio;
            await sender.setParameters(parameters);

            console.log(`[Initial Bitrate] AUDIO: ${(optimalBitrate.audio / 1000).toFixed(0)}kbps | Quality: ${this.getConnectionQuality().toUpperCase()}`);
            this.log(`Audio bitrate set to ${optimalBitrate.audio / 1000}kbps`);
          }
        } catch (error) {
          console.error('Error setting bitrate parameters:', error);
        }

        if (track.kind === 'audio' || track.kind === 'video') {
          this.socket?.emit('stream:track:change_status', {
            room: this.roomId,
            streamId: stream.id,
            streamType: this.streamTypes[stream.id],
            manual_id: this.streamManual[stream.id],
            trackId: track.id,
            status: track.enabled,
            type: track.kind,
          });
        }

      });

    }

    async updateLocalStreams(streamIndex: string, newStream: MediaStream) {
      // Get all local streams
      const streams = this.localMediaStreams;

      // Create a map of the new stream tracks to easily find them
      const newTracksMap = new Map<string, MediaStreamTrack>();
      newStream.getTracks().forEach((track) => {
        newTracksMap.set(track.kind, track);
      });

      // Get the stream to update and update the tracks
      const stream = streams.find(s => s.id === streamIndex);

      if (stream) {
        // If the stream has no track, then add the new tracks
        if (stream.getTracks().length === 0) {
          const index = streams.findIndex((streamSelected) => streamSelected.id === streamIndex);

          // Remove the old stream and add the new one
          this.removeLocalStream(index);
          this.addLocalStream(newStream);
        }

        stream.getTracks().forEach((track) => {
          const newTrack = newTracksMap.get(track.kind);
          if (newTrack) {
            stream.removeTrack(track);
            stream.addTrack(newTrack);

            if (newTrack.kind === 'video'){
              this.events.onUpdateLocalStream && this.events.onUpdateLocalStream(newTrack);
            }

            Object.keys(this.participants).forEach((userId) => {

              this.participants[userId].peer.forEach((peer) => {

                peer.getSenders().forEach((sender) => {

                  // Only replace if track kinds match (video with video, audio with audio)
                  if ((sender.track?.id === track.id || !sender.track) && sender.track?.kind === newTrack.kind) {

                    sender.replaceTrack(newTrack);
                  }

                });

              });
            });
          }
        });
      }
    }

    removeParticipants() {

        Object.keys(this.participants).forEach((userId) => {

            this.participants[userId].peer.forEach((peer) => {

                peer.close();

            });

        });

        this.participants = {}
        this.senders = {}
        this.events?.onUpdateParticipants?.(this.participants);
    }
    emit(event: string, data: any) {

        this.socket?.emit(event, data);
    }
    disconnect() {

        this.socket?.disconnect();

        // close all peer connections
        Object.keys(this.participants).forEach((userId) => {

            this.participants[userId].peer.forEach((peer) => {

                peer.close();

            });

        });

    }
    removeLocalStreams() {
        // remove event
        this.localMediaStreams.forEach((stream, index) => {

            this.events?.onRemoveLocalStream?.(index);

        });
        this.localMediaStreams = []
    }

    async removeShareScreen(stream: MediaStream) {
      // get stream
      const index = this.localMediaStreams.findIndex((streamSelected) => streamSelected.id === stream.id);

      // remove stream
      this.removeLocalStream(index);
    }

    async removeLocalStream(streamIndex: number) {
        // get stream
        const stream = this.localMediaStreams[streamIndex];

        const tracksIds = stream.getTracks().map((track) => track.id);
        // stop stream
        stream.getTracks().forEach((track) => {

            track.stop();
            // stop rtc
            this.localMediaStreams[streamIndex].removeTrack(track);
        });

        this.events?.onRemoveLocalStream?.(streamIndex);
        this.socket?.emit('stream:remove', {
            room: this.roomId,
            streamId: this.localMediaStreams[streamIndex].id,
        });
        // remove local stream from localMediaStreams array
        this.localMediaStreams.splice(streamIndex, 1);

        // remove stream from all participants
        Object.keys(this.participants).forEach((userId) => {

            this.participants[userId].peer.forEach((peer) => {

              peer.getSenders().forEach((sender) => {

                  if (tracksIds.includes(sender.track?.id || '')) {

                      peer.removeTrack(sender);
                  }

              });
            });

        })
    }

    setRoom(newRoom: string | undefined = 'jajaja') {
        //@ts-ignore
        window['EnovaitRoomId'] = newRoom;
        this.roomId = newRoom;
    }

    async requestRecordingName(): Promise<string> {
      const defaultName = 'Grabacion de video';
      let recordingName = defaultName;

      if (!recordingName) {
        recordingName = defaultName;
      }

      recordingName = recordingName.replace(/[^a-zA-Z0-9]/g, '_');

      return `${recordingName}_${new Date().toISOString().replace(/[:.-]/g, '')}`;
    }

    async addLocalStream(stream: MediaStream, type?: {type: string}, manual?: {manual_id: string}) {

        this.localMediaStreams.push(stream);
        this.streamTypes[stream.id] = type?.type || '';
        this.streamManual[stream.id] = manual?.manual_id || '';
        stream && stream?.getVideoTracks()?.[0]?.addEventListener?.('ended', () => {

            // remove stream
            // get index of stream
            const index = this.localMediaStreams?.findIndex((streamSelected) => streamSelected.id === stream.id);

            index > -1 && this.removeLocalStream(index);

        })

        const { group }  = JSON.parse(localStorage.getItem('user') || '{}');

        const recordingEnabled = localStorage.getItem('canRecord') === 'true' ? true : false;

        if (recordingEnabled) {
            let recordingName = '';

            // Use adaptive bitrate for recording based on connection quality
            const optimalBitrate = this.getOptimalBitrate();

            // record: Configuración optimizada para audio médico de alta fidelidad (inspirado en emurmur)
            const mediaRecorder = new MediaRecorder(stream, {
                audioBitsPerSecond: optimalBitrate.audio,
                videoBitsPerSecond: optimalBitrate.video,
                mimeType: 'video/webm; codecs=vp9',
            });

            console.log(`[Recording] Started with ${optimalBitrate.video / 1000}kbps video, ${optimalBitrate.audio / 1000}kbps audio`);

            // Get first path folder of window
            const randomString = Math.random().toString(36).substring(7);
            mediaRecorder.ondataavailable = async (event: any) => {
                this.localCall = localStorage.getItem('localCall') === 'true' ? true : false;
                // @ts-ignore
                if (event.data && event.data.size > 0 && !this.localCall) {
                    // @ts-ignore
                    if (window['EnovaitRoomId'] && window['EnovaitRoomId'] !== 'jajaja') {
                        if (!recordingName) {
                          recordingName = await this.requestRecordingName();
                        }

                        this.socket && this.socket.emit('media:record', {
                          // @ts-ignore
                          roomId: window['EnovaitRoomId'],
                          cameraId: randomString,
                          group: group || undefined,
                          data: event.data,
                          recordName: recordingName,
                        })
                    }

                }

            };

            // This will trigger a dataavailable event every second.
            mediaRecorder.start(5000);

            // This will trigger a single dataavailable event.
            mediaRecorder.requestData();

            // stop stream detect
            stream.getTracks().forEach((track) => {

                track.addEventListener('ended', () => {
                    mediaRecorder.stop();

                });

            }
            );

        }

        // share local streams to all remote users
        this.events.onNewLocalStream && this.events.onNewLocalStream(stream, type, manual);
        this.participants && Object.keys(this.participants).forEach(async (participantId) => {

            const randomString = Math.random().toString(36).substring(7);
            const peer = this.createPeerConnection(participantId, randomString, stream, type, manual);
            // @ts-ignore
            peer.id = randomString;
            this.participants[participantId].peer.push(peer);
            try {

                // have audio:
                const streamWithAudio = await stream.getAudioTracks().length > 0

                // have video:
                const streamWithVideo = await stream.getVideoTracks().length > 0

                const localSdp = await peer.createOffer({
                    iceRestart: true,
                    offerToReceiveAudio: streamWithAudio,
                    offerToReceiveVideo: streamWithVideo,
                });

                // Modify SDP to request lower bitrate from remote peer
                const modifiedSdp = this.modifySdpBitrate(localSdp);

                try {

                    await peer.setLocalDescription(new RTCSessionDescription(modifiedSdp));
                    this.socket?.emit('rtcp:offer', {
                        sdp: localSdp,
                        offerSendID: this.socket?.id,
                        peerId: randomString,
                        // random string
                        user: this.user,
                        offerReceiveID: participantId,
                    });

                }
                catch (e) {

                    console.error('Error setLocalDescription', e)

                }

            } catch (e) {

                console.error(e);

            }

        })

    }

    handleErrors(error: string, data: any) {

        this.log(error, data)

    }

    connect() {

        try {

            // Detect if it's a satellite connection based on stored quality or assume worst case
            const isSatellite = true; // Assume satellite until proven otherwise

            const socket = io(`${environment.dish.host}`, {
                secure: true,
                autoConnect: true,
                // Satellite connections need much longer timeouts
                timeout: isSatellite ? 60000 : 30000,  // 60s for satellite, 30s for terrestrial
                reconnectionDelay: isSatellite ? 5000 : 3000,  // Wait longer between reconnections
                reconnectionDelayMax: isSatellite ? 30000 : 10000,  // Max backoff for satellite
                reconnectionAttempts: isSatellite ? 50 : 20,  // More attempts for unstable connections
                // Randomize reconnection delay to avoid thundering herd
                randomizationFactor: 0.5,
                path: '/socket/socket.io',
                extraHeaders: {
                    'Access-Control-Allow-Origin': '*', // Required for CORS support to work
                },
                // Important: Allow more time for transport upgrades in high latency
                transports: ['websocket', 'polling'],  // Try websocket first, fallback to polling
                upgrade: true,
                rememberUpgrade: true
            });

            if (socket) {

                console.log(`[Socket.IO] Connection config: timeout=${isSatellite ? '60s' : '30s'}, attempts=${isSatellite ? 50 : 20}, delay=${isSatellite ? '5s' : '3s'}`);
                return socket;

            }

            else {

                this.log('error connecting', false);

            }

        }
        catch (e) {

            console.error(e);
            this.log('error connecting', e, false);

        }

    }

    joinRoom(room?: string) {
        if (room) this.roomId = room;
        try {
            this.socket?.emit('room:join', {
                room: this.roomId,
                user: this.user,
            });
        }
        catch (e) {

            this.log('error room join', e, false);

        }

    }

    onReconnect() {

        this.socket?.on('reconnect_attempt', (attempt: any) => {

            this.log(`reconnect attempt ${attempt}`);

        });

    }

    onError() {

        this.socket?.on('error', (error: any) => {

            this.log('error', error, false);
            console.error(error);

        })

    }

    onConnectFailed() {

        this.socket?.on('connect_failed', (error: any) => {

            this.log('connect failed', error, false);

        })

        // on internet is too low
        this.socket?.on('reconnect_failed', (error: any) => {



        })

    }

    onConnectError() {

        this.socket?.on('connect_error', (error: any) => {

            this.events.onErrorConnecting && this.events.onErrorConnecting();

        })

    }

    onConnect() {

        this.socket?.on('connect', () => {

            // change socket status
            this.status = SocketStatus.CONNECTED;

            // outside event
            this.events.onConnect && this.events.onConnect();

            this.log(`Socket ID [${this.socket?.id}]`);
            this.socket?.id && this.events.onSocketId && this.events.onSocketId(this.socket?.id);

            // Start latency ping interval
            this.startPingInterval();

            this.socket && this.events && new SocketAvailable({
                socket: this.socket,
                user: this.user,
                events: {
                    closeSession: this.events.closeSession,
                    onAvailables: this.events.onAvailables,
                    onRoom: this.events.onRoom,
                    onCall: this.events.onCall,
                    onAccepted: this.events.onAccepted,
                    onRejected: this.events.onRejected,
                    onCanceled: this.events.onCanceled,
                    askCloseSession: this.events.askCloseSession,
                    onChangeLayout: this.events.onChangeLayout,
                    onRemoveStream: this.events.onRemoveStream,
                    onChangeStreamStatus: this.events.onChangeStreamStatus,
                    onFileShare: this.events.onFileShare,
                    onChangeStreamTrackStatus: this.events.onChangeStreamTrackStatus,
                    onChangeVolume: this.events.onChangeVolume,
                    onNoLongerActive: this.events.onNoLongerActive,
                    onPtzDevicesListReceived: this.events.onPtzDevicesListReceived,
                    onPtzCommandMove: this.events.onPtzCommandMove,
                    onPtzCapabilitiesReceived: this.events.onPtzCapabilitiesReceived,
                    onMindrayCredentialsAvailable: this.events.onMindrayCredentialsAvailable,
                    onMindrayCredentialsList: this.events.onMindrayCredentialsList,
                }
            })

        });

    }

    sendMessage(message: string) {

        this.socket?.emit('chat:message', {
            room: this.roomId,
            message: message,
            name: this.user.name + ' ' + this.user.surnames,
            date: new Date().getTime(),
            senderSocketId: this.socket?.id,
        });

    }

    onMessage() {

        this.socket?.on('chat:message', (data: any) => {
            if (data.senderSocketId !== this.socket?.id) {
                const audio = new Audio(messageAudio);
                audio.volume = 0.3;
                audio.play();
            }

            const message: ChatMessageType = {
                room: data.room,
                message: data.message,
                name: data.name,
                date: data.date,
                socketId: data.senderSocketId,
                type: data.senderSocketId === this.socket?.id ? 'me' : 'other',
            }

            this.events.onMessage && this.events.onMessage(message);

        })

    }

    onDisconnect() {

        this.socket?.on('disconnect', () => {

            // change socket status
            this.status = SocketStatus.DISCONNECTED;

            // Stop latency ping interval
            this.stopPingInterval();

            // outside event
            this.events.onDisconnect && this.events.onDisconnect();

        })

    }

    onAllUsers() {

        this.socket?.on('room:all_users', (allUsers: {
            socketId: string,
            user: UserType
        }[]) => {

            allUsers.forEach(async (user) => {
                // Validate if user is not me
                if (user.user.id === this.user?.id) return;

                // Save user:
                if (!this.participants[user.socketId]) this.participants[user.socketId] = { peer: [], streams: [], user: user.user }
                // update all users
                this.events.onUpdateParticipants && this.events.onUpdateParticipants(this.participants);

                // Send all streams to new user
                this.localMediaStreams && this.localMediaStreams.forEach(async (stream) => {

                    const randomString = Math.random().toString(36).substring(7);
                    const peer = this.createPeerConnection(user.socketId, randomString, stream);
                    // @ts-ignore
                    peer.id = randomString;
                    this.participants[user.socketId].peer.push(peer);

                    try {

                        // have audio:
                        const streamWithAudio = await stream.getAudioTracks().length > 0
                        // have video:
                        const streamWithVideo = await stream.getVideoTracks().length > 0
                        const localSdp = await peer.createOffer({
                            iceRestart: true,
                            offerToReceiveAudio: streamWithAudio,
                            offerToReceiveVideo: streamWithVideo,
                        });

                        // Modify SDP to request lower bitrate from remote peer
                        const modifiedSdp = this.modifySdpBitrate(localSdp);

                        await peer.setLocalDescription(new RTCSessionDescription(modifiedSdp));
                        this.socket?.emit('rtcp:offer', {
                            sdp: localSdp,
                            offerSendID: this.socket.id,
                            offerReceiveID: user.socketId,
                            peerId: randomString,

                        });

                    } catch (e) {

                        console.error(e);

                    }

                });

            });

        });
        this.socket?.on('room:new_user', (user: { socketId: string; user: UserType }) => {

            // Validate if user is not me
            if (user.user.id === this.user?.id) return;

            // Save user:
            if (!this.participants[user.socketId]) this.participants[user.socketId] = {
                peer: [], streams: [], user: user.user
            }

            // update all users
            this.events.onUpdateParticipants && this.events.onUpdateParticipants(this.participants);


            this.localMediaStreams && this.localMediaStreams.forEach(async (stream) => {

                const randomString = Math.random().toString(36).substring(7);
                const peer = this.createPeerConnection(user.socketId, randomString, stream);
                // @ts-ignore
                peer.id = randomString;
                this.participants[user.socketId].peer.push(peer);

                try {

                    // have audio:
                    const streamWithAudio = await stream.getAudioTracks().length > 0
                    // have video:
                    const streamWithVideo = await stream.getVideoTracks().length > 0

                    const localSdp = await peer.createOffer({
                        iceRestart: true,
                        offerToReceiveAudio: streamWithAudio,
                        offerToReceiveVideo: streamWithVideo,
                    });

                    // Modify SDP to request lower bitrate from remote peer
                    const modifiedSdp = this.modifySdpBitrate(localSdp);

                    await peer.setLocalDescription(new RTCSessionDescription(modifiedSdp));
                    this.socket?.emit('rtcp:offer', {
                        sdp: localSdp,
                        offerSendID: this.socket.id,
                        peerId: randomString,

                        offerReceiveID: user.socketId,
                    });

                } catch (e) {

                    console.error(e);

                }

            });

        });

    }

    getOffer() {

        this.socket?.on(
            'rtcp:offer',
            async (data: {
                sdp: RTCSessionDescription;
                offerSendID: string;
                offerSendEmail: string;
                peerId: string;
                user: UserType
            }) => {

                const { sdp, offerSendID, peerId, } = data;
                // search peerId
                // @ts-ignore

                const peer = this.createPeerConnection(offerSendID, peerId);

                // @ts-ignore
                peer.id = peerId;
                if (!this.participants[offerSendID]) {
                    this.participants[offerSendID] = { peer: [], streams: [], user: data.user }
                }
                this.participants[offerSendID].peer.push(peer);

                try {

                    await peer.setRemoteDescription(new RTCSessionDescription(sdp));
                    const localSdp = await peer.createAnswer();

                    // Modify SDP to request lower bitrate from remote peer
                    const modifiedSdp = this.modifySdpBitrate(localSdp);

                    await peer.setLocalDescription(new RTCSessionDescription(modifiedSdp));
                    this.socket?.emit('rtcp:answer', {
                        sdp: localSdp,
                        answerSendID: this.socket.id,
                        peerId: peerId,
                        answerReceiveID: offerSendID,
                    });

                } catch (e) {

                    console.error(e);

                }

            },
        );

        this.socket?.on(
            'rtcp:answer',
            (data: { sdp: RTCSessionDescription; peerId: string; answerSendID: string }) => {

                const { sdp, answerSendID, peerId, } = data;

                this.participants[answerSendID].peer.forEach((peer) => {

                    // @ts-ignore

                    //@ts-ignore
                    if (peer.id === peerId) {

                        if (!peer) return;
                        if (!sdp) return
                        try {

                            peer.setRemoteDescription(new RTCSessionDescription(sdp));

                        }
                        catch (e) {

                            console.error('Error  getanswer')
                            console.error(e)

                        }

                    }

                })

            },
        );

        this.socket?.on(
            'rtcp:candidate',
            async (data: { user: UserType, peerId: string, candidate: RTCIceCandidateInit; candidateSendID: string }) => {

                const { candidate, candidateSendID } = data;

                if (!this.participants[candidateSendID]) {
                    this.participants[candidateSendID] = { peer: [], streams: [], user: data.user }
                }

                this.participants[candidateSendID].peer.forEach((peer) => {

                    try {

                        // @ts-ignore
                        if (peer.id === data.peerId && candidate) peer.addIceCandidate(new RTCIceCandidate(candidate));

                    }
                    catch (e) {

                        console.error(e)

                    }

                })

            },
        );

        this.socket?.on('room:user_left', (data: { socketId: string }) => {

            if (this.participants[data.socketId]) {

                // close peer connection
                this.participants[data.socketId]?.peer?.forEach((peer) => {

                    peer.close();

                });
                delete this.participants[data.socketId];
                this.events.onUpdateParticipants && this.events.onUpdateParticipants(this.participants);

                // Remove stream from all participants
                Object.keys(this.participants).forEach((userId) => {
                    this.participants[userId].peer.forEach((peer) => {

                        peer.getSenders().forEach((sender) => {

                            if (sender.track?.id === data.socketId) {

                                peer.removeTrack(sender);

                            }

                        });

                    });

                });

            }
            return

        });

        this.socket?.on('stream:removed_remote', (data: { room: string, streamId: string }) => {

            // map this.participants and remove stream with same id
            Object.keys(this.participants).forEach((key) => {

                this.participants[key].streams = this.participants[key].streams.filter((stream) => stream.id !== data.streamId);

            });

            // search stream delete and update participants
            this.events?.onRemoveStreamParticipant && this.events.onRemoveStreamParticipant(data.streamId);

        });

    }

    createRTC() {
        // Import environment configuration for ICE servers
        const { environmentConfig } = require('../../../environment');
        const iceServers = environmentConfig.getIceServers();
        const quality = this.getConnectionQuality();
        const isSatelliteConnection = quality === 'moderate' || quality === 'bad';

        const rtc: RTCPeerConnection = new RTCPeerConnection(
            {
                // Use ICE servers from environment configuration
                iceServers: iceServers,
                iceTransportPolicy: 'relay',
                // Optimize for high latency satellite connections
                bundlePolicy: 'max-bundle',  // Reduce ports/bandwidth usage
                rtcpMuxPolicy: 'require',     // Multiplex RTP and RTCP on same port
                iceCandidatePoolSize: isSatelliteConnection ? 0 : 10,  // Reduce ICE overhead on satellite
            }
        );

        // Configurar códecs de audio optimizados para dispositivos médicos (inspirado en emurmur)
        rtc.addEventListener('negotiationneeded', async () => {
            try {
                // Obtener transceiver de audio para configurar códecs de alta fidelidad
                const audioTransceiver = rtc.getTransceivers().find(t => t.receiver.track?.kind === 'audio');
                if (audioTransceiver) {
                    // Configurar parámetros de códec para preservar máximo rango de frecuencias
                    const params = audioTransceiver.sender.getParameters();
                    if (params.encodings && params.encodings.length > 0) {
                        params.encodings[0].maxBitrate = 320000;  // MEJORADO: Bitrate más alto para fidelidad médica
                        params.encodings[0].priority = 'high';    // Alta prioridad para audio médico crítico
                        // Configuraciones adicionales para audio médico de alta calidad
                        (params.encodings[0] as any).networkPriority = 'high'; // NUEVO: Prioridad de red alta
                        await audioTransceiver.sender.setParameters(params);

                        console.log('🩺 Configured high-fidelity medical audio codec parameters:', params.encodings[0]);
                    }
                }
            } catch (error) {
                console.warn('Could not configure advanced audio codec parameters:', error);
            }
        });


        if (isSatelliteConnection) {
          console.log('[WebRTC] Satellite mode enabled: Optimized for high latency / low bandwidth');
        }
        return rtc;

    }

    createPeerConnection(userId: string, randomString: string, streamLocal?: MediaStream, type?: {type: string}, manual?: {manual_id: string}) {
        const pc = this.createRTC();

        /**
         * Format RTCP Stats to only send the necessary data to the server
         *
         * @param stats
         * @param type - video or audio stats
         * @param fields - fields to send to the server
         */
        const formatRtcpStats = (stats: RTCStatsReport, types: String[]) => {
          const defaultTypes = ['inbound-rtp', 'outbound-rtp', 'remote-inbound-rtp', 'remote-outbound-rtp', 'codec', 'track', 'transport'];
          const formattedStats: { [key: string]: any } = {};

          if (types.length === 0) {
            types = defaultTypes;
          }

          // Get the stats for the specific type
          const filteredStats = Array.from(stats.values()).filter((stat) => types.includes(stat.type));

          return filteredStats;
        }

        // Collect stats with adaptive interval based on connection quality
        // Bad connections: reduce stats frequency to save bandwidth
        const getStatsInterval = () => {
          const quality = this.getConnectionQuality();
          switch(quality) {
            case 'excellent': return 3000;  // 3s
            case 'good': return 5000;       // 5s
            case 'moderate': return 10000;  // 10s - reduce overhead
            case 'bad': return 20000;       // 20s - minimize bandwidth
            default: return 5000;
          }
        };

        const statsInterval = setInterval(async () => {
            const videoStats = await pc.getStats(streamLocal?.getVideoTracks()[0]);
            const audioStats = await pc.getStats(streamLocal?.getAudioTracks()[0]);

            // Send video stats
            if (videoStats) {
              this.socket?.emit('rtcp:stats', 'video', {
                user: userId,
                room: this.roomId,
                type: 'RTCStatsReport',
                kind: 'video',
                stats: formatRtcpStats(videoStats, []),
              });
            }

            // Send audio stats
            if (audioStats) {
              this.socket?.emit('rtcp:stats', 'audio', {
                user: userId,
                room: this.roomId,
                type: 'RTCStatsReport',
                kind: 'audio',
                stats: formatRtcpStats(audioStats, []),
              });
            }

        }, getStatsInterval());

        pc.onicecandidate = (e) => {

            if (!e.candidate) return;

            this.socket?.emit('rtcp:candidate', {
                candidate: e.candidate,
                candidateSendID: this.socket.id,
                peerId: randomString,
                user: this.user,
                candidateReceiveID: userId,
            });

        };

        pc.onicegatheringstatechange = (e) => {
            // console.log('=============')
            // console.log('ERROR #2:', e)
            // console.log('=============')
        };

        pc.oniceconnectionstatechange = async (e) => {

            const state = pc.signalingState;

            // console.log('=============')
            // console.log('oniceconnectionstatechange:', state)
            // console.log('iceconnectionstatechange:', pc.iceConnectionState)
            // console.log(pc)
            // console.log('=============')

            // disconnect:
            if (pc.iceConnectionState === 'disconnected') {
              // pc.close();
              // delete this.participants[userId];
              // this.events.onUpdateParticipants && this.events.onUpdateParticipants(this.participants);

              // TODO: We must send some 'ping' to the socket to check if the connection is still alive or not
            }
        };

        pc.ontrack = (e) => {

            // console.log('onbtrack', e)
            // console.log('par', this.participants)
            // console.log('ontrack:', userId)
            // console.log('par came:', this.participants[userId])

            if (e.streams && e.streams.length > 0) {

                e.streams.forEach((stream) => {

                    // detect track is stream exist
                    if (this.participants[userId].streams.find((s) => s.id === stream.id)) return;

                    // add stream to participants
                    this.participants[userId].streams?.push(stream);
                    this.events.onUpdateParticipants && this.events.onUpdateParticipants(this.participants);

                });

            }

        };

        streamLocal && this.shareLocalStreamsToRemoteUser(pc, streamLocal, type, manual);

        // eslint-disable-next-line max-lines
        return pc;

    }

    log(message: any, data?: any, status = true) {

        this.events.onLog && this.events.onLog({ status: status, log: message, date: new Date() });

    }
}
